import { Component, OnInit } from '@angular/core';
import { TariffSheetRequest, TariffSheetResponseDTO } from '../model/tariffs-model';
import { TariffsStore } from '../services/tariffs.store';
import {
  TYPE_TARIFF_SHEET_DISCOUNT_COMMERCE,
  TYPE_TARIFF_SHEET_CLIENT_INTEREST,
  TYPE_TARIFF_SHEET_CI_CO_AUTO,
  TYPE_TARIFF_SHEET_LEASING_AUTO,
  TYPE_TARIFF_SHEET_IRREGULAR_QUOTA,
} from '@core/constants/tariffSheet.constants';
import jsPDF from 'jspdf';
import autoTable, { CellInput, RowInput } from 'jspdf-autotable';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ResponseDialogComponent } from '@app/@shared/dialogs/response/response-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { ExcelService } from '@app/@shared/services/print/excel.service';
import { DatePipe } from '@angular/common';
import moment from 'moment';
import FileSaver from 'file-saver';

@Component({
  selector: 'app-ci-co',
  templateUrl: './ci-co.component.html',
  styleUrls: ['./ci-co.component.scss'],
})
export class CiCoComponent implements OnInit {
  tariffSheetRequest: TariffSheetRequest;
  tariffSheetResponse: TariffSheetResponseDTO;

  constructor(
    private tariffsStore: TariffsStore,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private excelService: ExcelService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {}

  getCiCOAuto(tariffSheetResponse: TariffSheetRequest) {
    this.tariffSheetRequest = tariffSheetResponse;
    this.tariffSheetRequest.typeTariffSheet = TYPE_TARIFF_SHEET_CI_CO_AUTO;

    this.tariffSheetResponse = null;
    this.tariffsStore
      .getTariffSheetTable(this.tariffSheetRequest)
      .then((data) => {
        if (data) {
          if (data.tariffSheetResponse.tariff[0].typeReturnTariffSheet !== TYPE_TARIFF_SHEET_CI_CO_AUTO) {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.width = '100%';
            dialogConfig.maxWidth = '500px';
            dialogConfig.data = {
              title: this.translateService.instant('tariffs.tariffTarThree.title'),
              text: this.translateService.instant(data.tariffSheetResponse.popupMsg),
              buttonCancel: false,
              noRedirect: true,
            };
            dialogConfig.disableClose = true;

            const modalDialog = this.dialog.open(ResponseDialogComponent, dialogConfig);
            modalDialog.afterClosed().subscribe(() => {
              this.tariffSheetResponse = data;
            });
          } else {
            this.tariffSheetResponse = data;
          }
        }
      })
      .catch((error) => console.error(error));
  }

  createPdf() {
    try {
      let doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [296.926, 209.804],
      });

      doc.setProperties({
        title: this.translateService.instant('tariffs.discount.excel.title'),
      });

      const widthColspan = 12;

      const returnType = this.tariffSheetResponse.tariffSheetResponse.tariff[0].typeReturnTariffSheet;

      const date = new Date();
      const dateFormat = moment().locale('es').format('DDMMYYYY');

      let headers: string[] = [];
      let fileName = '';

      switch (returnType) {
        case TYPE_TARIFF_SHEET_CI_CO_AUTO:
          fileName = this.translateService.instant('tariffs.cico.excel.title') + dateFormat;
          headers = [...this.tariffsStore.getHeadersCiCo()].map((header) => this.translateService.instant(header));
          break;
        case TYPE_TARIFF_SHEET_LEASING_AUTO:
          fileName = this.translateService.instant('tariffs.leasing.excel.title') + dateFormat;
          headers = [...this.tariffsStore.getHeadersLeasing()].map((header) => this.translateService.instant(header));
          break;
        case TYPE_TARIFF_SHEET_IRREGULAR_QUOTA:
          fileName = this.translateService.instant('tariffs.leasing.irregularQuotas.title') + dateFormat;
          headers = [...this.tariffsStore.getHeadersIrregularQuotas()].map((header) =>
            this.translateService.instant(header)
          );
          break;
        default:
          console.error('Type Not Found');
      }

      doc.setProperties({
        title: fileName,
      });

      const rowHead: RowInput = [{ content: '', colSpan: 4 }];
      const rowBody: RowInput = [
        {
          content: [headers?.join('\n')],
          colSpan: 4,
          styles: {
            cellWidth: widthColspan * 2,
          },
        },
      ];

      this.tariffSheetResponse.tariffSheetResponse.tariff[0].listTerms.forEach((tariffSheet) => {
        rowHead.push({ content: tariffSheet.strPlazos, colSpan: 4 });

        if (returnType === TYPE_TARIFF_SHEET_CI_CO_AUTO) {
          const grayColumn = [' ', ' ', ' ', tariffSheet.strTinT, ' ', ' ', ' ', ' ', ' '];
          const grayValue = grayColumn.join('\n');

          rowBody.push({
            content: grayValue,
            colSpan: 2,
            styles: {
              cellWidth: widthColspan,
              fillColor: '#969696',
            },
          } as CellInput);

          const whiteColumn = [
            tariffSheet.strCoefAnio1Vp,
            tariffSheet.strIncVp,
            tariffSheet.strCoefAnio1V,
            tariffSheet.strIncV,
            '',
            '',
            tariffSheet.strCoefAnio1Ss,
            tariffSheet.strIncSs,
            tariffSheet.strPorape,
          ];
          const whiteValue = whiteColumn.join('\n');

          rowBody.push({
            content: whiteValue,
            colSpan: 2,
            styles: {
              minCellWidth: widthColspan,
            },
          } as CellInput);
        } else if (returnType === TYPE_TARIFF_SHEET_LEASING_AUTO) {
          const grayColumn = [' ', ' ', ' ', tariffSheet.strTinT, ' '];
          const grayValue = grayColumn.join('\n');

          rowBody.push({
            content: grayValue,
            colSpan: 2,
            styles: {
              cellWidth: widthColspan,
              fillColor: '#969696',
            },
          } as CellInput);

          const whiteColumn = [
            tariffSheet.strCoefAnio1Ss,
            tariffSheet.strIncSs,
            tariffSheet.strCoefAnio2Ss,
            tariffSheet.strIncSs,
            tariffSheet.strPorape,
          ];
          const whiteValue = whiteColumn.join('\n');

          rowBody.push({
            content: whiteValue,
            colSpan: 2,
            styles: {
              minCellWidth: widthColspan,
            },
          } as CellInput);
        } else if (returnType === TYPE_TARIFF_SHEET_IRREGULAR_QUOTA) {
          const grayColumn = [
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
            tariffSheet.strTinT,
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
          ];
          const grayValue = grayColumn.join('\n');

          rowBody.push({
            content: grayValue,
            colSpan: 2,
            styles: {
              cellWidth: widthColspan,
              fillColor: '#969696',
            },
          } as CellInput);

          const whiteColumn = [
            ' ',
            tariffSheet.strCoefAnio1Vp,
            tariffSheet.strCoefAnio2Vp,
            tariffSheet.strCoefRestoVp,
            tariffSheet.strIncVp,
            ' ',
            ' ',
            tariffSheet.strCoefAnio1V,
            tariffSheet.strCoefAnio2V,
            tariffSheet.strCoefRestoV,
            tariffSheet.strIncV,
            ' ',
            ' ',
            tariffSheet.strCoefAnio1Ss,
            tariffSheet.strCoefAnio2Ss,
            tariffSheet.strCoefRestoSs,
            tariffSheet.strIncSs,
            tariffSheet.strPorape,
          ];
          const whiteValue = whiteColumn.join('\n');

          rowBody.push({
            content: whiteValue,
            colSpan: 2,
            styles: {
              minCellWidth: widthColspan,
            },
          } as CellInput);
        }
      });

      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;

      autoTable(doc, {
        startY: 62,
        tableWidth: 'auto',
        rowPageBreak: 'avoid',
        showHead: 'everyPage',
        head: [rowHead],
        headStyles: { fillColor: '#969696', halign: 'center', valign: 'middle', fontSize: 7 },
        body: [rowBody],
        bodyStyles: {
          fontSize: 5.4,
        },
        didDrawCell: (data) => {
          const cell = data.cell;
          const width = cell.width;
          const height = cell.height;
          doc.rect(cell.x, cell.y, width, height);
        },
        didDrawPage: () => {
          /* HEADER */
          doc.setFontSize(9);
          doc.text(this.datePipe.transform(new Date(), 'MMM d, y, h:mm:ss'), pageWidth - 14 * 4, 22);

          var img = new Image();
          img.src = 'assets/img/logo.PNG';
          doc.addImage(img, 'png', 3, 5, 70, 25);

          /* HEADER */
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');

          doc.setDrawColor(0);
          doc.setTextColor('#FFFFFF');
          doc.setFillColor('#969696');

          doc.rect(14, 30, pageWidth - 28, 8, 'F');
          doc.text(
            this.tariffSheetRequest.commercialProduct.value + ' ' + this.tariffSheetRequest.establishment.value,
            pageWidth / 2,
            36,
            { align: 'center' }
          );

          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');

          doc.setDrawColor(0);
          doc.setTextColor('#FFFFFF');
          doc.setFillColor('#969696');

          doc.rect(14, 42, pageWidth - 28, 6, 'F');
          doc.text(this.translateService.instant('tariffs.header'), pageWidth / 2, 46, {
            align: 'center',
          });

          doc.setFontSize(12);
          doc.setFont(undefined, 'normal');
          doc.setTextColor('#0000EE');

          doc.text(
            'www.sabadellconsumer.com',
            pageWidth / 2,
            54,
            { align: 'center' },
            { url: 'https://www.sabadellconsumer.com/' }
          );

          doc.setFontSize(8);
          doc.setFont(undefined, 'bold');
          doc.setTextColor('#0000EE');

          doc.setDrawColor(0);
          doc.setTextColor('#FFFFFF');
          doc.setFillColor('#969696');

          doc.rect(14 + widthColspan * 2, 57, pageWidth - (28 + widthColspan * 2), 4, 'F');
          doc.text(
            this.tariffSheetRequest.tariffs[0].code + ' - ' + this.tariffSheetRequest.tariffs[0].description,
            pageWidth / 2,
            60,
            { align: 'center' }
          );

          doc.setFont(undefined, 'normal');
          doc.setTextColor('#000000');

          /* FOOTER */
          //Properties
          doc.setFontSize(8);

          doc.rect(14, pageHeight - (28 + 25), pageWidth - 28, 25);
          doc.text(this.translateService.instant('tariffs.tariffsvn.footer'), 15, pageHeight - (28 + 25) + 3);
          doc.text(this.translateService.instant('tariffs.ageVehicle.footer'), 15, pageHeight - (28 + 25) + 6);
          doc.text(this.translateService.instant('tariffs.spotCoefficient.footer'), 15, pageHeight - (28 + 25) + 9);

          doc.text(this.translateService.instant('tariffs.operations.footer'), 15, pageHeight - (28 + 25) + 18);

          doc.setFont(undefined, 'bold');
          doc.setTextColor('#FFFFFF');
          doc.setFillColor('#969696');
          doc.rect(14, pageHeight - (14 + 10), pageWidth - 28, 10, 'F');
          const phone = this.tariffSheetResponse.userDataTariffDTO[0].phone;
          doc.text(
            this.translateService.instant('excel.contactPerson') +
              ' ' +
              this.tariffSheetResponse.userDataTariffDTO[0].socialReason +
              ' ' +
              this.translateService.instant('excel.phone') +
              ' ' +
              phone?.substring(0, 3) +
              ' ' +
              phone?.substring(3, 6) +
              ' ' +
              phone?.substring(6, 9) +
              ' ' +
              this.translateService.instant('excel.mail') +
              ' ' +
              this.tariffSheetResponse.userDataTariffDTO[0].email?.toUpperCase(),
            pageWidth / 2,
            pageHeight - (14 + 10) + 6,
            { align: 'center' }
          );
          doc.setFont(undefined, 'normal');
        },
      });

      var blob = doc.output('blob');
      FileSaver.saveAs(blob, fileName);
      window.open(URL.createObjectURL(blob));
    } catch (error) {
      console.error(error);
    }
  }

  createExcel() {
    const returnType = this.tariffSheetResponse.tariffSheetResponse.tariff[0]?.typeReturnTariffSheet;
    const title = this.tariffSheetRequest.commercialProduct.value + ' ' + this.tariffSheetRequest.establishment.value;
    const subtitle = this.translateService.instant('tariffs.header');

    const dataTable = this.excelService.generateNewData(
      this.tariffSheetResponse.tariffSheetResponse.tariff[0].listTerms
    );

    const phone = this.tariffSheetResponse.userDataTariffDTO[0].phone;
    const contact =
      this.translateService.instant('excel.contactPerson') +
      ' ' +
      this.tariffSheetResponse.userDataTariffDTO[0].socialReason +
      ' ' +
      this.translateService.instant('excel.phone') +
      ' ' +
      phone?.substring(0, 3) +
      ' ' +
      phone?.substring(3, 6) +
      ' ' +
      phone?.substring(6, 9) +
      ' ' +
      this.translateService.instant('excel.mail') +
      ' ' +
      this.tariffSheetResponse.userDataTariffDTO[0].email?.toUpperCase();
    const tariff = this.tariffSheetRequest.tariffs[0].code + ' - ' + this.tariffSheetRequest.tariffs[0].description;
    const date = new Date();
    const dateFormat = moment().locale('es').format('DDMMYYYY');

    let filename = '';
    let rowHeaders: string[] = [];
    let properties: string[] = [];
    let grayPosition = 0;

    const description =
      this.translateService.instant('tariffs.tariffsvn.footer') +
      '\n' +
      this.translateService.instant('tariffs.ageVehicle.footer') +
      '\n' +
      this.translateService.instant('tariffs.spotCoefficient.footer') +
      '\n\n' +
      this.translateService.instant('tariffs.operations.footer');

    if (returnType === TYPE_TARIFF_SHEET_CI_CO_AUTO) {
      filename = this.translateService.instant('tariffs.cico.excel.title') + dateFormat;
      rowHeaders = this.tariffsStore.getHeadersCiCo();
      properties = [
        'strCoefAnio1Vp',
        'strIncVp',
        'strCoefAnio1V',
        'strIncVp',
        '',
        '',
        'strCoefAnio1Ss',
        'strIncSs',
        'strPorape',
      ];
      grayPosition = 3;
    } else if (returnType === TYPE_TARIFF_SHEET_LEASING_AUTO) {
      filename = this.translateService.instant('tariffs.leasing.excel.title') + dateFormat;
      rowHeaders = this.tariffsStore.getHeadersLeasing();
      properties = ['strCoefAnio1Ss', 'strIncSs', 'strCoefAnio2Ss', 'strIncSs', 'strPorape'];
      grayPosition = 3;
    } else if (returnType === TYPE_TARIFF_SHEET_IRREGULAR_QUOTA) {
      filename = this.translateService.instant('tariffs.leasing.irregularQuotas.title') + dateFormat;
      rowHeaders = this.tariffsStore.getHeadersIrregularQuotas();
      properties = [
        '',
        'strCoefAnio1Vp',
        'strCoefAnio2Vp',
        'strCoefRestoVp',
        'strIncVp',
        '',
        '',
        'strCoefAnio1V',
        'strCoefAnio2V',
        'strCoefRestoV',
        'strIncV',
        '',
        '',
        'strCoefAnio1Ss',
        'strCoefAnio2Ss',
        'strCoefRestoSs',
        'strIncSs',
        'strPorape',
      ];
      grayPosition = 8;
    } else {
      console.error('Type not found');
    }

    const excelData = {
      fileName: filename,
      title: title,
      subtitle: subtitle,
      tableHeaders: this.tariffSheetResponse.tariffSheetResponse.tariff[0].listTerms.map((term) => term.strPlazos),
      tableBody: dataTable,
      description: description,
      contact: contact,
      tariff: tariff,
      rowHeaders: rowHeaders,
      properties: properties,
      position: grayPosition,
    };

    this.excelService.exportExcelTariffSheet(excelData);
  }

  isTariffSheetValid() {
    const typeTariff = this.tariffSheetResponse?.tariffSheetResponse?.tariff[0]?.typeReturnTariffSheet;
    return (
      this.tariffSheetResponse &&
      typeTariff !== TYPE_TARIFF_SHEET_DISCOUNT_COMMERCE &&
      typeTariff !== TYPE_TARIFF_SHEET_CLIENT_INTEREST
    );
  }
}
